import React, { useMemo, useState, useRef, useEffect } from 'react'; // 1. Importar useRef y useEffect

/* React redux */
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import {
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

/* Project */
import { onChangeImage, controlsChanged, setControlsErrors } from '../schedulerSteps.actions';
import SchedulerStepsStyles from '../SchedulerSteps.styles';
import InformationDialog from './InformationDialog';
import { brandsLogos } from '../data/brandsLogos';
import isBrandSelectable from '../data/brandSelection';
import CustomAutoComplete from './CustomAutoComplete';

/* While the plate search runs it may pick the brand itself, so manual picks wait for it. */
const blockedBrandStyle = (searchingVehicle) =>
  searchingVehicle ? SchedulerStepsStyles.brandPaperBlocked : {};

function BrandsCards() {
  const [openDialog, setopenDialog] = useState(false); // TODO: DELETE STATE AND USE REDUX STORE
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef(null);
  const brands = useSelector((state) => state.app.objects.brands);
  const brandExternalId = useSelector((state) => state.schedulerSteps.controls.brandExternalId);
  const brandId = useSelector((state) => state.schedulerSteps.controls.brandId);
  const brandIdError = useSelector((state) => state.schedulerSteps.controlsErrors.brandId);
  const searchingVehicle = useSelector((state) => state.schedulerSteps.searchingVehicle);

  useEffect(() => {
    if (brandIdError && scrollRef.current) {
      scrollRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
    }
  }, [brandIdError]);

  const brandsSelectData = [];
  const brandsData = useMemo(
    () =>
      Object.keys(brands)
        .map((key) => brands[key])
        .filter((brand) => !brandsSelectData.includes(brand.name?.toLowerCase())),
    [brands, brandsSelectData],
  );

  const desiredOrder = [
    'Chevrolet',
    'Toyota',
    'Nissan',
    'Jeep',
    'Ram',
    'Mitsubishi',
    'Fiat',
    'Jmc',
    'Gac',
    'Omoda',
    'SsangYong',
    'Exeed',
    'Chery',
    'Byd',
    'Kia',
    'Renault'
  ];

  const reorderArray = (array, order) => {
    const ordered = [];
    const remaining = [...array];

    order.forEach((desiredName) => {
      const index = remaining.findIndex(
        (item) => item.name.toLowerCase() === desiredName.toLowerCase(),
      );

      if (index !== -1) {
        ordered.push(remaining[index]);
        remaining.splice(index, 1);
      }
    });

    ordered.push(...remaining);

    return ordered;
  };

  const reorderedArray = reorderArray(brandsData, desiredOrder);

  const brandValidation = (brand) =>
    brandsData.every((item) => item.name !== brand) ? 'Debe seleccionar una marca' : '';

  const onChange = (brand) => {
    if (!brand) {
      dispatch(onChangeImage({ id: '', name: '', externalId: '' }));
      return;
    }
    const errorMessage = brandValidation(brand.name);
    dispatch(
      setControlsErrors({
        controlsErrors: {
          brandId: errorMessage,
        },
      }),
    );
    dispatch(onChangeImage(brand));
  };

  const onClickOthers = () => {
    dispatch(controlsChanged({ name: 'brand', value: 'others' }));
    setopenDialog(true);
  };

  const handleCloseDialog = () => {
    setopenDialog(false);
    dispatch(controlsChanged({ name: 'brand', value: '' }));
  };

  if (isMobile) {
    return (
      <Grid ref={scrollRef}>
        <FormControl fullWidth>
          <CustomAutoComplete
            name="brandId"
            value={brandId.toString()}
            options={brandsData}
            disabled={searchingVehicle}
            size="small"
            label="Seleccionar una marca"
            error={!!brandIdError}
            getNewValue={(brand) =>
              onChange(brandsData.find((item) => item.id === brand.id))?.name || ''
            }
          />
          <FormHelperText error={!!brandIdError}>{brandIdError}</FormHelperText>
        </FormControl>
      </Grid>
    );
  }

  return (
    <>
      <Grid ref={scrollRef} sx={SchedulerStepsStyles.brandsContainer}>
        {reorderedArray.filter(isBrandSelectable).map((brand) => (
          <Paper
            key={brand.externalId}
            sx={[
              brandExternalId === brand.externalId
                ? SchedulerStepsStyles.brandPaperSelected
                : SchedulerStepsStyles.brandPaper,
              blockedBrandStyle(searchingVehicle),
            ]}
            aria-disabled={searchingVehicle}
            onClick={() => onChange(brand)}>
            <Grid>
              <img
                alt={`${brand.name}-logo`}
                src={brandsLogos[brand.name]}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '180px',
                  objectFit: 'contain',
                }}
              />
            </Grid>
          </Paper>
        ))}

        <Paper
          sx={[SchedulerStepsStyles.brandPaper, blockedBrandStyle(searchingVehicle)]}
          aria-disabled={searchingVehicle}
          onClick={onClickOthers}>
          <MoreHorizIcon sx={SchedulerStepsStyles.moreIcon} />
          <Typography variant="body2" sx={{ color: '#999' }} gutterBottom>
            Otros
          </Typography>
        </Paper>
      </Grid>
      {!!brandIdError && (
        <FormHelperText sx={{ mt: 2 }} error={!!brandIdError}>
          Debe seleccionar una marca
        </FormHelperText>
      )}
      <InformationDialog open={openDialog} handleClose={handleCloseDialog} type="otherBrand" />
    </>
  );
}

export default BrandsCards;
